import React from 'react'

import { Box, Typography } from '@mui/material'
import { CellChip } from 'features/table-columns/cells'
import { BillingsParams } from 'models/Billings'
import { RecurrenceBonusCarryover } from 'models/Recurrences'
import moment from 'moment'

import { Table } from 'components/Table'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import * as S from '../ModalRecurrenceDetail.styles'

interface BonusCarryoverDetailProps {
  carryover: RecurrenceBonusCarryover
  billings: BillingsParams[]
  onOpenBilling: (uuid: string) => void
}

// Conteúdo do accordion "Saldo de bonificação" (SC-11262/SC-11766): o resumo do
// saldo e, ciclo a ciclo, onde ele já foi aplicado ou está previsto.
const BonusCarryoverDetail = ({
  carryover,
  billings,
  onOpenBilling
}: BonusCarryoverDetailProps) => {
  const until = carryover.valid_until_cycle

  const infoRows: { label: string; value: string }[] = [
    {
      label: carryover.expired ? 'Saldo expirado' : 'Saldo atual',
      value: formatMoney(carryover.remaining)
    },
    { label: 'Saldo gerado na entrada', value: formatMoney(carryover.initial) },
    { label: 'Já aplicado', value: formatMoney(carryover.applied) },
    ...(!carryover.expired && carryover.planned > 0
      ? [
          {
            label: 'Previsto nos próximos ciclos',
            value: formatMoney(carryover.planned)
          }
        ]
      : []),
    // O que sobra depois das previsões se perde ao fim da validade.
    ...(!carryover.expired && carryover.unused > 0
      ? [
          {
            label: `Sem uso previsto (perde após o ciclo ${until})`,
            value: formatMoney(carryover.unused)
          }
        ]
      : []),
    // Saldo de item que deixou de valer: o item foi removido ou não aparece em
    // nenhum ciclo até a validade (o resumo não traz o motivo).
    ...(carryover.forfeited > 0
      ? [
          {
            label: 'Perdido (item fora dos próximos ciclos)',
            value: formatMoney(carryover.forfeited)
          }
        ]
      : []),
    { label: 'Válido até', value: `Ciclo ${until}` }
  ]

  const rows = carryover.by_cycle.map((entry) => {
    // A cobrança do ciclo; uma cancelada só entra se não houver outra.
    const cycleBillings = billings.filter(
      (billing) => Number(billing.recurrenceCycle) === entry.cycle
    )
    const billing =
      cycleBillings.find(
        (b) => !['canceled', 'cancelled'].includes(b.status || '')
      ) || cycleBillings[0]

    return {
      uuid: billing?.uuid || `cycle-${entry.cycle}`,
      clickable: !!billing?.uuid,
      cycle: `Ciclo ${entry.cycle}`,
      dueDate: billing?.dueDate
        ? moment(billing.dueDate, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : '—',
      amount: formatMoney(entry.amount),
      status:
        entry.status === 'applied' ? (
          <CellChip label="Aplicado" tone="success" />
        ) : (
          <CellChip label="Previsto" tone="neutral" />
        )
    }
  })

  return (
    <>
      {infoRows.map(({ label, value }) => (
        <Box
          key={label}
          display="flex"
          gap={1.25}
          alignItems="end"
          margin={'10px 0px 10px 0px'}
        >
          <Typography variant="smLight" color={theme.palette.neutral[400]}>
            {label}
          </Typography>
          <S.Separator sx={{ marginTop: '4px' }} />
          <Typography variant="smBold">{value}</Typography>
        </Box>
      ))}

      {rows.length > 0 && (
        <Box mt={2}>
          <Typography variant="smBold">Aplicação por ciclo</Typography>
          <Box mt={1} maxHeight="300px" overflow="auto">
            <Table
              mobileCards
              header={[
                { id: 'cycle', label: 'Ciclo' },
                { id: 'dueDate', label: 'Vencimento' },
                { id: 'amount', label: 'Valor abatido' },
                { id: 'status', label: 'Status' }
              ]}
              rows={rows}
              rowAction={onOpenBilling}
              hoverInRow
              select={{ useSelect: false }}
              hiddePagination
              hideLastRowSeparator
            />
          </Box>
        </Box>
      )}
    </>
  )
}

export default BonusCarryoverDetail
